import type { ListBuilderConfiguration } from '../types';

/**
 * Voice category value mapping
 */
const VOICE_CATEGORY_VALUE_MAPPING = [
	{ value: 'cloned' },
	{ value: 'premade' },
	{ value: 'generated' },
	{ value: 'professional' },
];

/**
 * Gender value mapping
 */
const GENDER_VALUE_MAPPING = [
	{ value: 'male' },
	{ value: 'female' },
	{ value: 'neutral' },
];

/**
 * Favorite star value mapping. The voice profile API returns the entity's
 * `isFavorite`, a boolean since PD-14802; the 0/1 spellings stay for rows
 * served from a cache written before that.
 */
const FAVORITE_VALUE_MAPPING = [
	{ value: 'false', iconName: 'far fa-star', color: { themeColor: 'textMuted' } },
	{ value: 'true', iconName: 'fas fa-star', color: { themeColor: 'warning' } },
	{ value: '0', iconName: 'far fa-star', color: { themeColor: 'textMuted' } },
	{ value: '1', iconName: 'fas fa-star', color: { themeColor: 'warning' } },
];

/**
 * Conversation Voice Profile list configuration
 * Shows available voice profiles in the system
 * Converted from JSON to TypeScript for type safety
 */
export const CONVERSATION_VOICE_PROFILE_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-voices-all',
			cacheTTL: 600000,
			name: 'All Voices',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'audio-preview',
					width: 60,
					minWidth: 60,
					maxWidth: 60,
					fields: [
						{
							id: 'audio-player-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'id',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'audio_player',
						},
					],
					headers: [{ id: 'h-audio', text: '' }],
					sortable: false,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
				{
					id: 'voice-info',
					fields: [
						{
							id: 'favorite-star',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isFavorite',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'value-mapping',
									valueMapping: FAVORITE_VALUE_MAPPING,
								},
							},
							displayStyle: 'icon',
						},
						{
							id: 'voice-name',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'conversation_voice_profile',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
								textExpansion: { enabled: false },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'description',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								ellipsis: true,
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Voice' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'name',
							objectTable: 'conversation_voice_profile',
						},
					],
					verticalAlignment: 'top',
				},
				{
					id: 'voice-characteristics',
					fields: [
						{
							id: 'voice-category',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'category',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'value-mapping',
									valueMapping: VOICE_CATEGORY_VALUE_MAPPING,
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'spacing-category',
							dataConfig: { type: 'custom_text', customText: ' ' },
							styleConfig: { size: 'xs', colorRule: { type: 'default' } },
							displayStyle: 'text',
						},
						{
							id: 'use-case',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'useCase',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'warning' },
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'gender',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'gender',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'value-mapping',
									valueMapping: GENDER_VALUE_MAPPING,
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'spacing-2',
							dataConfig: { type: 'custom_text', customText: ' ' },
							styleConfig: { size: 'xs', colorRule: { type: 'default' } },
							displayStyle: 'text',
						},
						{
							id: 'age-range',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'ageRange',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'static',
									staticColor: { type: 'neutral' },
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'Characteristics' }],
					sortable: false,
					alignment: 'left',
					verticalAlignment: 'top',
				},
				{
					id: 'voice-language-accent',
					fields: [
						{
							id: 'language',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'language',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-lang',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'accent',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'accent',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h3', text: 'Language & Accent' }],
					sortable: false,
					alignment: 'left',
					verticalAlignment: 'top',
				},
				{
					id: 'voice-popularity',
					fields: [
						{
							id: 'likes-count',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'likedByCount',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'likes-label',
							dataConfig: { type: 'custom_text', customText: ' likes' },
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-3',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'clones-count',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clonedByCount',
									objectTable: 'conversation_voice_profile',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'clones-label',
							dataConfig: { type: 'custom_text', customText: ' clones' },
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h4', text: 'Popularity' }],
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'likedByCount',
							objectTable: 'conversation_voice_profile',
						},
					],
					verticalAlignment: 'top',
				},
			],
			description: 'All available voice profiles in the system',
			defaultSortId: 'column-voice-info-asc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [
			'voiceSearch',
			'voiceCategory',
			'useCase',
			'gender',
			'voiceLanguages',
			'accent',
			'ageRange',
			'isFavorite',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/ai-agents/voices/:id',
			objectLabel: 'Voice Profile',
			objectTable: 'conversation_voice_profile',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'previewUrl', fieldLabel: 'Preview URL' },
				{ fieldName: 'category', fieldLabel: 'Category' },
				{ fieldName: 'useCase', fieldLabel: 'Use Case' },
				{ fieldName: 'gender', fieldLabel: 'Gender' },
				{ fieldName: 'language', fieldLabel: 'Language' },
				{ fieldName: 'accent', fieldLabel: 'Accent' },
				{ fieldName: 'ageRange', fieldLabel: 'Age Range' },
				{ fieldName: 'isFavorite', fieldLabel: 'Favorite' },
				{ fieldName: 'likedByCount', fieldLabel: 'Likes' },
				{ fieldName: 'clonedByCount', fieldLabel: 'Clones' },
				{ fieldName: 'createdDate', fieldLabel: 'Created' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Sort voices by name',
		},
		{
			id: 'favorites',
			name: 'Favorites First',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'isFavorite',
					objectTable: 'conversation_voice_profile',
				},
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Show favorite voices first',
		},
		{
			id: 'most-liked',
			name: 'Most Liked',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'likedByCount',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Sort by likes from ElevenLabs',
		},
		{
			id: 'most-cloned',
			name: 'Most Cloned',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'clonedByCount',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Sort by clones from ElevenLabs',
		},
		{
			id: 'created',
			name: 'Recently Added',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'createdDate',
					objectTable: 'conversation_voice_profile',
				},
			],
			description: 'Sort by creation date',
		},
	],
};
